import { useEffect, useMemo } from 'react';

import { AppEvents } from '@grafana/data';
import { t } from '@grafana/i18n';
import { getAppEvents } from '@grafana/runtime';
import { type QueryList } from 'app/extensions/api/clients/queries/v1/endpoints.gen';
import { type SavedQueryBase } from 'app/features/explore/QueryLibrary/types';

import { useLoadQueryMetadata, useLoadUsers } from '../utils/dataFetching';
import { convertDataQueryResponseToSavedQueriesDTO } from '../utils/mappers';

export const useGetSavedQueries = (queries?: QueryList) => {
  const data = useMemo(() => (queries ? convertDataQueryResponseToSavedQueriesDTO(queries) : undefined), [queries]);
  const loadUsersResult = useLoadUsersWithError(data);

  // The users lookup is "settled" once it has resolved, errored, or was skipped (skipToken leaves it
  // uninitialized). Metadata enrichment must wait for this so it doesn't render before names load,
  // but must NOT wait forever when there are simply no users to resolve (empty library).
  const usersSettled = loadUsersResult.isUninitialized || loadUsersResult.isSuccess || loadUsersResult.isError;

  return useLoadQueryMetadataWithError(data, loadUsersResult.data, usersSettled);
};

/**
 * Wrap useLoadUsers with error handling.
 * @param data
 */
function useLoadUsersWithError(data: SavedQueryBase[] | undefined) {
  const userUIDs = useMemo(() => data?.map((qt) => qt.user?.uid).filter((uid) => uid !== undefined), [data]);
  const loadUsersResult = useLoadUsers(userUIDs);

  useEffect(() => {
    if (loadUsersResult.error) {
      getAppEvents().publish({
        type: AppEvents.alertError.name,
        payload: [
          t('query-library.user-info.error', 'Error attempting to get user info from the library: {{error}}', {
            error: JSON.stringify(loadUsersResult.error),
          }),
        ],
      });
    }
  }, [loadUsersResult.error]);
  return loadUsersResult;
}

/**
 * Wrap useLoadQueryMetadata with error handling.
 * @param queryTemplates
 * @param userDataList
 */
function useLoadQueryMetadataWithError(
  queryTemplates: SavedQueryBase[] | undefined,
  userDataList: ReturnType<typeof useLoadUsers>['data'],
  usersSettled: boolean
) {
  const result = useLoadQueryMetadata(queryTemplates, userDataList, usersSettled);

  // useLoadQueryMetadata keeps rows whose datasource couldn't be resolved (e.g. it was deleted) in the list,
  // carrying a `loadError` flag so the user can still see and delete them. That state is surfaced per-row in
  // the UI (broken badge + detail banner), so we deliberately don't raise a global toast for it — the list
  // itself loaded successfully, and a toast would misleadingly read as a whole-list failure.
  return {
    loading: result.loading,
    error: result.error,
    value: result.loading ? undefined : result.value,
  };
}
